// Change #100462 - Standard / Enterprise billing, chosen by CRMit only.
//
// Rendered only for developers (CRMit staff), and the backend ignores the
// fields from anyone else, so a customer can never pick it. Standard is the
// default and sends nothing, exactly as before.
//
// Enterprise creates the company's own plan: the users agreed with the
// customer (MaxUsers) and a tolerance above that (the hard limit) so they are
// not blocked while the commercials are discussed.

import React from "react";
import { Label } from "reactstrap";

export const ENTERPRISE = "ENTERPRISE";

/** Returns an error message, or "" when the billing fields are fine. */
export const validateBillingMode = (data) => {
  if (data?.SubscriptionBillingMode !== ENTERPRISE) return "";
  const max = Number(data?.EnterpriseMaxUsers);
  if (!(max > 0)) return "Enter how many users the Enterprise plan includes";
  const tol = data?.EnterpriseMaxUsersTolerance;
  if (tol !== "" && tol != null && Number(tol) < max) {
    return "The tolerance cannot be lower than the users included";
  }
  return "";
};

export default function BillingModeSection({ createCompanyData, setCreateCompanyData }) {
  const mode = createCompanyData?.SubscriptionBillingMode === ENTERPRISE ? ENTERPRISE : "STANDARD";
  const set = (patch) => setCreateCompanyData({ ...createCompanyData, ...patch });

  return (
    <div className="border rounded-3 p-3 mt-3 bg-light">
      <div className="d-flex align-items-center justify-content-between mb-2">
        <div className="fw-bold">
          <i className="ri-shield-star-line me-1 text-primary"></i>Billing
        </div>
        <span className="badge bg-primary-subtle text-primary">CRMit staff only</span>
      </div>
      <div className="btn-group w-100" role="group">
        <button
          type="button"
          className={`btn btn-sm ${mode === "STANDARD" ? "btn-primary" : "btn-outline-primary"}`}
          onClick={() =>
            set({
              SubscriptionBillingMode: undefined,
              EnterprisePlanName: undefined,
              EnterpriseMaxUsers: undefined,
              EnterpriseMaxUsersTolerance: undefined,
            })
          }
        >
          Standard
        </button>
        <button
          type="button"
          className={`btn btn-sm ${mode === ENTERPRISE ? "btn-primary" : "btn-outline-primary"}`}
          onClick={() =>
            set({
              SubscriptionBillingMode: ENTERPRISE,
              EnterprisePlanName: createCompanyData?.EnterprisePlanName ?? "",
              EnterpriseMaxUsers: createCompanyData?.EnterpriseMaxUsers ?? "",
              EnterpriseMaxUsersTolerance: createCompanyData?.EnterpriseMaxUsersTolerance ?? "",
            })
          }
        >
          Enterprise
        </button>
      </div>
      <div className="text-muted small mt-2">
        {mode === ENTERPRISE
          ? "The customer adds and removes their own users up to the limit below. They never buy licences or see prices; CRMit bills them."
          : "The customer buys licences by card, as normal."}
      </div>

      {mode === ENTERPRISE && (
        <div className="row g-2 mt-1">
          <div className="col-12">
            <Label className="mb-1">Plan name</Label>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder={`Enterprise - ${createCompanyData?.BusinessName || "business name"}`}
              value={createCompanyData?.EnterprisePlanName ?? ""}
              onChange={(e) => set({ EnterprisePlanName: e.target.value })}
            />
          </div>
          <div className="col-6">
            <Label className="mb-1">
              Users included <span className="text-danger">*</span>
            </Label>
            <input
              type="number"
              min={1}
              className="form-control form-control-sm"
              placeholder="e.g. 20"
              value={createCompanyData?.EnterpriseMaxUsers ?? ""}
              onChange={(e) => set({ EnterpriseMaxUsers: e.target.value })}
            />
          </div>
          <div className="col-6">
            <Label className="mb-1">Allow up to (tolerance)</Label>
            <input
              type="number"
              min={1}
              className="form-control form-control-sm"
              placeholder="e.g. 25"
              value={createCompanyData?.EnterpriseMaxUsersTolerance ?? ""}
              onChange={(e) => set({ EnterpriseMaxUsersTolerance: e.target.value })}
            />
          </div>
          <div className="col-12 text-muted small">
            Over the users included they get a warning and CRMit is notified; at the tolerance they are
            blocked. Blank tolerance = no headroom.
          </div>
        </div>
      )}
    </div>
  );
}
